import axios from "axios";
import {serviceUrl} from "@/services/ServiceUrl";
import store from "@/store";
import {auth} from "@/services/Auth";

interface ErrorLogEntry {
    service: string;
    category: string;
    error: string;
    routeName?: string | null;
    routePath?: string | null;
    method?: string | null;
    statusCode?: number | null;
    userCode?: string | null;
    userUuid?: string | null;
    userName?: string | null;
    zone?: string | null;
    roles?: string[] | null;
    requiredRoles?: string[] | string | null;
}

class ErrorLogger {
    private queue: ErrorLogEntry[] = [];
    private flushTimeout: any = null;
    private readonly FLUSH_DELAY = 2000;
    private readonly MAX_QUEUE = 20;
    // Dédup côté client pour les refus : évite de re-poster le même (route + rôles)
    // pendant la durée de vie de l'onglet (retour history, réessais, etc.).
    private denialSeen = new Set<string>();

    private getBaseEntry(): Partial<ErrorLogEntry> {
        const user = store.state.mainStore?.user;
        const ct = store.getters.currentTenant?.();
        const token = store.state.mainStore?.userToken;
        const jwt = token ? auth.parseJwt(token) : {};
        const code = user?.email ?? jwt.username ?? jwt.code ?? null;
        const displayName = user?.name ? `${user.name} ${user.prename ?? ''}`.trim() : (user?.email ?? jwt.username ?? user?.code ?? null);
        return {
            service: 'FRONTEND',
            userCode: code,
            userUuid: user?.uuid ?? jwt.uuid ?? null,
            userName: displayName,
            zone: ct?.code?.toLowerCase() ?? null,
        };
    }

    logAxiosError(error: any): void {
        if (!error?.response && !error?.message) return;

        const config = error.config ?? {};
        const response = error.response;
        let category = 'FRONTEND_ERROR';

        if (response?.status === 403) category = 'ACCESS_DENIED';
        else if (response?.status === 401) category = 'AUTH_FAILURE';
        else if (response?.status >= 500) category = 'SERVER_ERROR';
        else if (!response) category = 'NETWORK_ERROR';

        const entry: ErrorLogEntry = {
            ...this.getBaseEntry(),
            category,
            method: config.method?.toUpperCase() ?? null,
            routePath: config.url ?? null,
            statusCode: response?.status ?? null,
            error: response?.data?.message ?? response?.data?.detail ?? error.message ?? 'Unknown error',
        } as ErrorLogEntry;

        if (category === 'ACCESS_DENIED') {
            entry.roles = auth.getRoles();
            const required = response?.data?.requiredRolesRaw ?? response?.data?.required ?? null;
            if (required) entry.requiredRoles = required;
            if (response?.data?.routeName) entry.routeName = response.data.routeName;
            if (this.alreadySeenDenial(entry)) return;
        }

        this.push(entry);
    }

    logJsError(error: Error | string, source?: string): void {
        const msg = error instanceof Error ? error.message : String(error);
        const stack = error instanceof Error ? error.stack : undefined;

        this.push({
            ...this.getBaseEntry(),
            category: 'JS_ERROR',
            routeName: source ?? null,
            routePath: window.location.pathname,
            error: stack ? `${msg}\n${stack.substring(0, 1500)}` : msg,
        } as ErrorLogEntry);
    }

    logRouteDenied(path: string, routeName: string | null, requiredRoles: string[], userRoles: string[], message?: string): void {
        const entry: ErrorLogEntry = {
            ...this.getBaseEntry(),
            category: 'ROUTE_DENIED',
            routeName,
            routePath: path,
            statusCode: 403,
            roles: userRoles,
            requiredRoles,
            error: message ?? 'Route access denied',
        } as ErrorLogEntry;
        if (this.alreadySeenDenial(entry)) return;
        this.push(entry);
    }

    // Clé = catégorie + route + rôles triés ; si déjà vu pendant cette session d'onglet, skip.
    private alreadySeenDenial(entry: ErrorLogEntry): boolean {
        const rolesKey = Array.isArray(entry.roles) ? [...entry.roles].sort().join(',') : '';
        const key = [entry.category, entry.routeName ?? entry.routePath ?? '', rolesKey].join('|');
        if (this.denialSeen.has(key)) return true;
        this.denialSeen.add(key);
        return false;
    }

    private push(entry: ErrorLogEntry): void {
        this.queue.push(entry);

        // Flush immédiat pour les catégories critiques : le user va être redirigé,
        // attendre 2s risque d'être coupé par un rechargement de page.
        const immediate = entry.category === 'ROUTE_DENIED' || entry.category === 'ACCESS_DENIED';
        if (immediate || this.queue.length >= this.MAX_QUEUE) {
            this.flush();
            return;
        }

        if (this.flushTimeout) clearTimeout(this.flushTimeout);
        this.flushTimeout = setTimeout(() => this.flush(), this.FLUSH_DELAY);
    }

    private flush(): void {
        if (this.flushTimeout) {
            clearTimeout(this.flushTimeout);
            this.flushTimeout = null;
        }
        if (this.queue.length === 0) return;

        const errors = [...this.queue];
        this.queue = [];

        // Ce fichier construisait son URL lui-meme : c'est lui qui restait bloque en
        // mixed content alors qu'Api.ts etait deja corrige.
        const authUrl = serviceUrl('auth', process.env.VUE_APP_AUTH_URL);
        if (!authUrl) return;

        const url = authUrl + 'activity/frontend-error';
        const token = store.state.mainStore?.userToken;
        const headers: any = {'Content-Type': 'application/json'};
        if (token) headers.Authorization = `Bearer ${token}`;

        // Fire-and-forget — on privilégie axios (envoie le JWT, visible dans devtools),
        // sendBeacon ne sert que de fallback (page unload, perte de token).
        try {
            axios.post(url, {errors}, {headers}).catch(() => {
                if (navigator.sendBeacon) {
                    try { navigator.sendBeacon(url, JSON.stringify({errors})); } catch (_) {}
                }
            });
        } catch (_) {
            if (navigator.sendBeacon) {
                try { navigator.sendBeacon(url, JSON.stringify({errors})); } catch (_) {}
            }
        }
    }
}

export const errorLogger = new ErrorLogger();
